<script>
import { useAlert } from 'dashboard/composables';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportsAPI from 'dashboard/api/reports';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';

// Visual treatment per delivery status. `problem` rows are highlighted.
const STATUS_META = {
  sent: { labelKey: 'SENT', tone: 'amber', problem: true },
  delivered: { labelKey: 'DELIVERED', tone: 'teal', problem: false },
  read: { labelKey: 'READ', tone: 'grass', problem: false },
  failed: { labelKey: 'FAILED', tone: 'ruby', problem: true },
};

const BADGE_CLASSES = {
  amber: 'bg-n-amber-3 text-n-amber-11',
  teal: 'bg-n-teal-3 text-n-teal-11',
  grass: 'bg-n-teal-3 text-n-teal-11',
  ruby: 'bg-n-ruby-3 text-n-ruby-11',
};

// WhatsApp Cloud API error codes we have a localized explanation for. The raw
// `external_error` is stored as "<code>: <title>"; anything outside this set
// falls back to showing the provider text verbatim.
const KNOWN_ERROR_CODES = new Set([
  '100',
  '190',
  '368',
  '470',
  '471',
  '1013',
  '80007',
  '130472',
  '131026',
  '131047',
  '131048',
  '131049',
  '131051',
  '131052',
  '131053',
  '132000',
  '132001',
  '132005',
  '132007',
  '132012',
  '132015',
  '132016',
  '133000',
  '133004',
  '133009',
  '133010',
  '133015',
]);

export default {
  name: 'MessageDeliveryReports',
  components: {
    ReportHeader,
    ReportFilterSelector,
  },
  data() {
    return {
      from: 0,
      to: 0,
      inboxId: null,
      // all | delivered | undelivered
      statusFilter: 'all',
      page: 1,
      summary: null,
      messages: [],
      meta: { current_page: 1, total_count: 0, total_pages: 0, per_page: 25 },
      isLoading: false,
    };
  },
  computed: {
    statusFilterOptions() {
      return [
        { value: 'all', label: this.$t('REPORT.MESSAGE_DELIVERY.FILTER.ALL') },
        {
          value: 'delivered',
          label: this.$t('REPORT.MESSAGE_DELIVERY.FILTER.DELIVERED'),
        },
        {
          value: 'undelivered',
          label: this.$t('REPORT.MESSAGE_DELIVERY.FILTER.UNDELIVERED'),
        },
      ];
    },
    summaryCards() {
      if (!this.summary) return [];
      const s = this.summary;
      return [
        {
          key: 'total',
          value: s.total,
          label: this.$t('REPORT.MESSAGE_DELIVERY.SUMMARY.TOTAL'),
          hint: null,
          tone: 'slate',
        },
        {
          key: 'delivered_total',
          value: s.delivered_total,
          label: this.$t('REPORT.MESSAGE_DELIVERY.SUMMARY.DELIVERED'),
          hint: this.$t('REPORT.MESSAGE_DELIVERY.SUMMARY.DELIVERED_HINT'),
          tone: 'teal',
        },
        {
          key: 'undelivered_total',
          value: s.undelivered_total,
          label: this.$t('REPORT.MESSAGE_DELIVERY.SUMMARY.UNDELIVERED'),
          hint: this.$t('REPORT.MESSAGE_DELIVERY.SUMMARY.UNDELIVERED_HINT'),
          tone: 'ruby',
        },
        {
          key: 'failed',
          value: s.failed,
          label: this.$t('REPORT.MESSAGE_DELIVERY.SUMMARY.FAILED'),
          hint: null,
          tone: 'ruby',
        },
      ];
    },
    rangeStart() {
      return (this.page - 1) * this.meta.per_page + 1;
    },
    rangeEnd() {
      return Math.min(this.page * this.meta.per_page, this.meta.total_count);
    },
  },
  methods: {
    onFilterChange({ from, to, selectedInbox }) {
      this.from = from;
      this.to = to;
      this.inboxId = selectedInbox?.id || null;
      this.page = 1;
      this.fetchReport();
    },
    onStatusFilterChange(value) {
      this.statusFilter = value;
      this.page = 1;
      this.fetchReport();
    },
    onPageChange(page) {
      if (page < 1 || page > this.meta.total_pages) return;
      this.page = page;
      this.fetchReport();
    },
    async fetchReport() {
      if (!this.from || !this.to) return;
      this.isLoading = true;
      try {
        const { data } = await ReportsAPI.getMessageDeliveryReport({
          from: this.from,
          to: this.to,
          inboxId: this.inboxId,
          status: this.statusFilter === 'all' ? undefined : this.statusFilter,
          page: this.page,
          perPage: this.meta.per_page,
        });
        this.summary = data.summary;
        this.messages = data.messages;
        this.meta = data.meta;
      } catch (error) {
        useAlert(this.$t('REPORT.MESSAGE_DELIVERY.FETCH_FAILED'));
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(timestamp) {
      return format(fromUnixTime(timestamp), 'dd/MM/yyyy HH:mm');
    },
    statusLabel(status) {
      const meta = STATUS_META[status];
      return meta
        ? this.$t(`REPORT.MESSAGE_DELIVERY.STATUS.${meta.labelKey}`)
        : status;
    },
    statusBadgeClass(status) {
      const meta = STATUS_META[status];
      return meta ? BADGE_CLASSES[meta.tone] : BADGE_CLASSES.amber;
    },
    isProblemRow(message) {
      const meta = STATUS_META[message.status];
      return meta ? meta.problem : false;
    },
    // Turns the raw provider error into a Portuguese explanation. Maps known
    // WhatsApp codes to a localized reason; unknown text is surfaced verbatim.
    translateError(raw) {
      const text = (raw || '').trim();
      if (!text) return '';
      const base = 'REPORT.MESSAGE_DELIVERY.ERRORS.CODES.';
      if (/template not found|invalid template/i.test(text)) {
        return this.$t(`${base}TEMPLATE_NOT_FOUND`);
      }
      const code = (text.match(/\b(\d{2,6})\b/) || [])[1];
      if (code && KNOWN_ERROR_CODES.has(code)) {
        return this.$t('REPORT.MESSAGE_DELIVERY.ERRORS.WITH_CODE', {
          reason: this.$t(`${base}${code}`),
          code,
        });
      }
      return this.$t('REPORT.MESSAGE_DELIVERY.ERRORS.PROVIDER', {
        detail: text,
      });
    },
    // Tooltip shown when hovering the status badge of a problem row.
    statusTooltip(message) {
      if (message.status === 'failed') {
        return (
          this.translateError(message.external_error) ||
          this.$t('REPORT.MESSAGE_DELIVERY.ERRORS.FAILED_GENERIC')
        );
      }
      if (message.status === 'sent') {
        if (!message.status_tracked) {
          return this.$t('REPORT.MESSAGE_DELIVERY.ERRORS.PENDING_UNTRACKED');
        }
        return (
          this.translateError(message.external_error) ||
          this.$t('REPORT.MESSAGE_DELIVERY.ERRORS.PENDING')
        );
      }
      return '';
    },
    // Opens the conversation scrolled to this specific message.
    openConversation(message) {
      if (!message.conversation_id) return;
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.$route.params.accountId,
          conversation_id: message.conversation_id,
        },
        query: { messageId: message.id },
      });
    },
  },
};
</script>

<template>
  <ReportHeader
    :header-title="$t('REPORT.MESSAGE_DELIVERY.HEADER')"
    :header-description="$t('REPORT.MESSAGE_DELIVERY.DESCRIPTION')"
  />
  <div class="flex flex-col flex-1 gap-6 max-w-[960px] mx-auto w-full">
    <ReportFilterSelector
      show-inbox-filter
      :show-business-hours-switch="false"
      @filter-change="onFilterChange"
    />

    <!-- Summary cards -->
    <div class="grid gap-3 grid-cols-2 md:grid-cols-4">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-1"
      >
        <span class="text-2xl font-semibold text-n-slate-12">
          {{ card.value.toLocaleString() }}
        </span>
        <span class="text-sm text-n-slate-11">{{ card.label }}</span>
        <span v-if="card.hint" class="text-xs text-n-slate-10">
          {{ card.hint }}
        </span>
      </div>
    </div>

    <!-- Status filter -->
    <div class="flex items-center gap-2">
      <button
        v-for="option in statusFilterOptions"
        :key="option.value"
        class="px-3 py-1.5 text-sm rounded-lg border transition-colors"
        :class="
          statusFilter === option.value
            ? 'bg-n-brand text-white border-n-brand'
            : 'bg-n-solid-1 text-n-slate-11 border-n-weak hover:border-n-slate-6'
        "
        @click="onStatusFilterChange(option.value)"
      >
        {{ option.label }}
      </button>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto border rounded-lg border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left border-b border-n-weak text-n-slate-11">
            <th class="px-4 py-3 font-medium">
              {{ $t('REPORT.MESSAGE_DELIVERY.TABLE.DATE') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ $t('REPORT.MESSAGE_DELIVERY.TABLE.CONTACT') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ $t('REPORT.MESSAGE_DELIVERY.TABLE.INBOX') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ $t('REPORT.MESSAGE_DELIVERY.TABLE.CONTENT') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ $t('REPORT.MESSAGE_DELIVERY.TABLE.STATUS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="message in messages"
            :key="message.id"
            class="border-b cursor-pointer border-n-weak last:border-b-0"
            :class="
              isProblemRow(message)
                ? 'bg-n-ruby-2 hover:bg-n-ruby-3'
                : 'hover:bg-n-alpha-1'
            "
            @click="openConversation(message)"
          >
            <td class="px-4 py-3 whitespace-nowrap text-n-slate-12">
              {{ formatDate(message.created_at) }}
            </td>
            <td class="px-4 py-3 text-n-slate-12">
              {{
                message.contact_name ||
                $t('REPORT.MESSAGE_DELIVERY.TABLE.NO_NAME')
              }}
            </td>
            <td class="px-4 py-3 text-n-slate-11">{{ message.inbox_name }}</td>
            <td class="px-4 py-3 text-n-slate-11 max-w-[220px] truncate">
              {{ message.content }}
            </td>
            <td class="px-4 py-3 whitespace-nowrap">
              <span
                v-tooltip="isProblemRow(message) ? statusTooltip(message) : ''"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="[
                  statusBadgeClass(message.status),
                  isProblemRow(message) ? 'cursor-help' : '',
                ]"
              >
                {{ statusLabel(message.status) }}
              </span>
              <span
                v-if="!message.status_tracked"
                v-tooltip="$t('REPORT.MESSAGE_DELIVERY.TABLE.UNTRACKED')"
                class="ml-1 i-lucide-info text-n-slate-10 size-3.5 align-middle"
              />
            </td>
          </tr>
          <tr v-if="!isLoading && !messages.length">
            <td colspan="5" class="px-4 py-8 text-center text-n-slate-10">
              {{ $t('REPORT.MESSAGE_DELIVERY.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="meta.total_count > 0"
      class="flex items-center justify-between text-sm text-n-slate-11"
    >
      <span>
        {{
          $t('REPORT.MESSAGE_DELIVERY.PAGINATION.SHOWING', {
            count: `${rangeStart}–${rangeEnd}`,
            total: meta.total_count.toLocaleString(),
          })
        }}
      </span>
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1.5 rounded-lg border border-n-weak bg-n-solid-1 disabled:opacity-40"
          :disabled="page <= 1"
          @click="onPageChange(page - 1)"
        >
          {{ $t('REPORT.MESSAGE_DELIVERY.PAGINATION.PREVIOUS') }}
        </button>
        <button
          class="px-3 py-1.5 rounded-lg border border-n-weak bg-n-solid-1 disabled:opacity-40"
          :disabled="page >= meta.total_pages"
          @click="onPageChange(page + 1)"
        >
          {{ $t('REPORT.MESSAGE_DELIVERY.PAGINATION.NEXT') }}
        </button>
      </div>
    </div>
  </div>
</template>
